import React, { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate, formatDateTime } from "@/lib/format";
import { DOCUMENT_CATEGORIES, VISIBILITIES, TONE, optOf, labelOf } from "@/config/documentOptions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  FolderLock, UploadCloud, Search, FileText, FileImage, File as FileIcon, Download,
  Trash2, Pencil, Eye, EyeOff, Users, Handshake, Building2, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

const iconFor = (ct) => {
  if (!ct) return FileIcon;
  if (ct.startsWith("image/")) return FileImage;
  if (ct.includes("pdf")) return FileText;
  return FileIcon;
};
const prettySize = (b) => {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const Pill = ({ arr, value, testid }) => {
  if (!value) return null;
  const o = optOf(arr, value);
  const t = TONE[o?.tone || "gray"];
  return (
    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold border whitespace-nowrap"
      style={{ backgroundColor: t.bg, color: t.color, borderColor: t.border }} data-testid={testid}>
      {o?.label || labelOf(arr, value)}
    </span>
  );
};

const MetricCard = ({ icon: Icon, label, value, accent, testid }) => (
  <Card className="p-3.5 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid={testid}>
    <div className="flex items-center gap-2.5">
      <span className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${accent}1A` }}>
        <Icon size={17} style={{ color: accent }} />
      </span>
      <div className="min-w-0">
        <div className="text-xl font-bold tabular-nums text-[#252221] leading-none">{value ?? 0}</div>
        <div className="text-[11px] text-muted-foreground mt-1 truncate">{label}</div>
      </div>
    </div>
  </Card>
);

const emptyUpload = { category: "other", visibility: "internal", notes: "", related: "none" };

export default function DocumentVault() {
  const { user } = useAuth();
  const canWrite = !user?.is_portal;

  const [docs, setDocs] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [leads, setLeads] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", category: "all", visibility: "all" });
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadMeta, setUploadMeta] = useState(emptyUpload);
  const [pendingFile, setPendingFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const [editForm, setEditForm] = useState({ category: "other", visibility: "internal", notes: "" });
  const inputRef = useRef();

  const loadMetrics = useCallback(() => { api.get("/documents/metrics").then((r) => setMetrics(r.data || {})).catch(() => {}); }, []);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.q) params.q = filters.q;
      if (filters.category !== "all") params.category = filters.category;
      if (filters.visibility !== "all") params.visibility = filters.visibility;
      const { data } = await api.get("/documents", { params });
      setDocs(data.items || []);
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => {
    loadMetrics();
    api.get("/leads", { params: { limit: 100 } }).then((r) => setLeads(r.data.items || [])).catch(() => {});
    api.get("/cases", { params: { limit: 100 } }).then((r) => setCases(r.data.items || [])).catch(() => {});
  }, [loadMetrics]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const refresh = () => { load(); loadMetrics(); };

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPendingFile(file);
    setUploadMeta(emptyUpload);
    setUploadOpen(true);
    if (inputRef.current) inputRef.current.value = "";
  };

  const doUpload = async (e) => {
    e.preventDefault();
    if (!pendingFile) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", pendingFile);
    fd.append("category", uploadMeta.category);
    fd.append("visibility", uploadMeta.visibility);
    if (uploadMeta.notes) fd.append("notes", uploadMeta.notes);
    if (uploadMeta.related !== "none") {
      const [type, id] = uploadMeta.related.split(":");
      fd.append("related_type", type);
      fd.append("related_id", id);
    }
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document added to the vault");
      setUploadOpen(false); setPendingFile(null); refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); } finally { setUploading(false); }
  };

  const openDoc = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not open file"); }
  };
  const archive = async (doc) => {
    try { await api.delete(`/documents/${doc.id}`); toast.success("Document archived"); refresh(); } catch { toast.error("Could not archive"); }
  };
  const openEdit = (doc) => { setEditDoc(doc); setEditForm({ category: doc.category || "other", visibility: doc.visibility || "internal", notes: doc.notes || "" }); };
  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/documents/${editDoc.id}`, editForm);
      toast.success("Document updated"); setEditDoc(null); refresh();
    } catch { toast.error("Could not update"); }
  };

  const DocCard = ({ d }) => {
    const I = iconFor(d.content_type);
    return (
      <div className="flex items-start gap-3 rounded-xl border border-[#E6EDF2] p-3 bg-white" data-testid="vault-document-card">
        <div className="h-11 w-11 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: "rgba(28,67,88,0.08)" }}>
          <I size={20} className="text-[#1C4358]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold truncate text-[#252221]">{d.file_name || d.original_filename}</div>
          <div className="flex flex-wrap items-center gap-1.5 mt-1">
            <Pill arr={DOCUMENT_CATEGORIES} value={d.category} testid="doc-category-pill" />
            <Pill arr={VISIBILITIES} value={d.visibility} testid="doc-visibility-pill" />
          </div>
          {d.notes && <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">{d.notes}</p>}
          <div className="text-[11px] text-muted-foreground mt-1.5">{d.uploader_name || "Team"} · {prettySize(d.size)} · {formatDate(d.created_at)}</div>
        </div>
        <div className="flex flex-col gap-1">
          <button onClick={() => openDoc(d)} className="p-1.5 rounded-lg hover:bg-[rgba(28,67,88,0.08)] text-[#1C4358]" data-testid="vault-view-button" title="View / download"><Download size={15} /></button>
          {canWrite && <button onClick={() => openEdit(d)} className="p-1.5 rounded-lg hover:bg-[rgba(28,67,88,0.08)] text-[#1C4358]" data-testid="vault-edit-button" title="Edit metadata"><Pencil size={15} /></button>}
          {canWrite && <button onClick={() => archive(d)} className="p-1.5 rounded-lg hover:bg-[rgba(156,30,34,0.08)] text-[#9C1E22]" data-testid="vault-archive-button" title="Archive"><Trash2 size={15} /></button>}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#252221] flex items-center gap-2"><FolderLock size={22} className="text-[#1C4358]" /> Document Vault</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Every family document in one secure place — categorized, with controlled visibility.</p>
        </div>
        {canWrite && (
          <Button onClick={() => inputRef.current?.click()} data-testid="vault-upload-button" className="text-white" style={{ backgroundColor: "var(--mmm-red)" }}>
            <UploadCloud size={17} className="mr-1.5" /> Upload document
          </Button>
        )}
        <input ref={inputRef} type="file" className="hidden" onChange={onPickFile} data-testid="vault-file-input" />
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-5" data-testid="vault-metrics">
        <MetricCard icon={FolderLock} label="Total documents" value={metrics.total} accent="#1C4358" testid="metric-total" />
        <MetricCard icon={ShieldCheck} label="Internal only" value={metrics.internal} accent="#4B5563" testid="metric-internal" />
        <MetricCard icon={Users} label="Family visible" value={metrics.family_visible} accent="#256B4B" testid="metric-family" />
        <MetricCard icon={Handshake} label="Partner visible" value={metrics.partner_visible} accent="#1C4358" testid="metric-partner" />
        <MetricCard icon={Building2} label="Purchaser visible" value={metrics.purchaser_visible} accent="#9A6516" testid="metric-purchaser" />
      </div>

      {/* Filters */}
      <Card className="p-3 mb-4 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
        <div className="flex flex-col lg:flex-row lg:items-center gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={filters.q} onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value }))} placeholder="Search file name or notes..." className="pl-9 h-9" data-testid="vault-search" />
          </div>
          <Select value={filters.category} onValueChange={(v) => setFilters((p) => ({ ...p, category: v }))}>
            <SelectTrigger className="h-9 w-full sm:w-[200px]" data-testid="filter-category"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-[320px]"><SelectItem value="all">All categories</SelectItem>{DOCUMENT_CATEGORIES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.visibility} onValueChange={(v) => setFilters((p) => ({ ...p, visibility: v }))}>
            <SelectTrigger className="h-9 w-full sm:w-[180px]" data-testid="filter-visibility"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Any visibility</SelectItem>{VISIBILITIES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </Card>

      <Tabs defaultValue="grid">
        <TabsList>
          <TabsTrigger value="grid" data-testid="tab-grid">All documents</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
        </TabsList>

        <TabsContent value="grid" className="mt-4">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}</div>
          ) : docs.length === 0 ? (
            <div className="py-16 text-center rounded-xl border border-[#E6EDF2] bg-white">
              <div className="mx-auto mb-3 h-14 w-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: "rgba(28,67,88,0.08)" }}><FolderLock size={26} className="text-[#1C4358]" /></div>
              <p className="font-medium text-[#252221]">No documents found</p>
              <p className="text-sm text-muted-foreground">Upload a document or adjust your filters.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="vault-grid">
              {docs.map((d) => <DocCard key={d.id} d={d} />)}
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-2">{docs.length} document{docs.length === 1 ? "" : "s"}</p>
        </TabsContent>

        <TabsContent value="timeline" className="mt-4">
          {loading ? (
            <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
          ) : docs.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No document activity yet.</p>
          ) : (
            <div className="relative pl-5" data-testid="vault-timeline">
              <div className="absolute left-[7px] top-1 bottom-1 w-px bg-[#E6EDF2]" />
              <div className="space-y-3">
                {docs.map((d) => (
                  <div key={d.id} className="relative">
                    <span className="absolute -left-[18px] top-2 h-3 w-3 rounded-full border-2 border-white" style={{ backgroundColor: "#1C4358" }} />
                    <div className="flex items-center justify-between gap-3 rounded-xl border border-[#E6EDF2] p-3 bg-white" data-testid="timeline-item">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-[#252221] truncate">{d.file_name || d.original_filename}</div>
                        <div className="flex items-center gap-1.5 mt-1"><Pill arr={DOCUMENT_CATEGORIES} value={d.category} /><Pill arr={VISIBILITIES} value={d.visibility} /></div>
                      </div>
                      <div className="text-[11px] text-muted-foreground whitespace-nowrap text-right">
                        <div>{formatDateTime(d.created_at)}</div>
                        <div>{d.uploader_name || "Team"}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Upload metadata dialog */}
      <Dialog open={uploadOpen} onOpenChange={(v) => { setUploadOpen(v); if (!v) setPendingFile(null); }}>
        <DialogContent className="max-w-lg" data-testid="vault-upload-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-lg flex items-center gap-2"><UploadCloud size={18} className="text-[#1C4358]" /> Add to Document Vault</DialogTitle>
            <DialogDescription>{pendingFile ? pendingFile.name : "Select a file"} · choose a category and who can see it.</DialogDescription>
          </DialogHeader>
          <form onSubmit={doUpload} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1.5 block text-[13px]">Category</Label>
                <Select value={uploadMeta.category} onValueChange={(v) => setUploadMeta((p) => ({ ...p, category: v }))}>
                  <SelectTrigger data-testid="upload-category"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-[300px]">{DOCUMENT_CATEGORIES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1.5 block text-[13px]">Visibility</Label>
                <Select value={uploadMeta.visibility} onValueChange={(v) => setUploadMeta((p) => ({ ...p, visibility: v }))}>
                  <SelectTrigger data-testid="upload-visibility"><SelectValue /></SelectTrigger>
                  <SelectContent>{VISIBILITIES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="mb-1.5 block text-[13px]">Related to (optional)</Label>
              <Select value={uploadMeta.related} onValueChange={(v) => setUploadMeta((p) => ({ ...p, related: v }))}>
                <SelectTrigger data-testid="upload-related"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-[280px]">
                  <SelectItem value="none">General (not linked)</SelectItem>
                  {cases.map((c) => <SelectItem key={c.id} value={`cases:${c.id}`}>{c.case_name || c.title || c.family_name || "Case"}</SelectItem>)}
                  {leads.map((l) => <SelectItem key={l.id} value={`leads:${l.id}`}>{l.name || [l.first_name, l.last_name].filter(Boolean).join(" ") || "Lead"}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="mb-1.5 block text-[13px]">Notes</Label><Textarea rows={2} value={uploadMeta.notes} onChange={(e) => setUploadMeta((p) => ({ ...p, notes: e.target.value }))} data-testid="upload-notes" /></div>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => { setUploadOpen(false); setPendingFile(null); }}>Cancel</Button>
              <Button type="submit" disabled={uploading} className="text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="confirm-upload-button">{uploading ? "Uploading..." : "Upload"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit metadata dialog */}
      <Dialog open={!!editDoc} onOpenChange={(v) => !v && setEditDoc(null)}>
        <DialogContent className="max-w-lg" data-testid="vault-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-lg flex items-center gap-2"><Pencil size={17} className="text-[#1C4358]" /> Edit document</DialogTitle>
            <DialogDescription>{editDoc?.file_name || editDoc?.original_filename}</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1.5 block text-[13px]">Category</Label>
                <Select value={editForm.category} onValueChange={(v) => setEditForm((p) => ({ ...p, category: v }))}>
                  <SelectTrigger data-testid="edit-category"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-[300px]">{DOCUMENT_CATEGORIES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1.5 block text-[13px]">Visibility</Label>
                <Select value={editForm.visibility} onValueChange={(v) => setEditForm((p) => ({ ...p, visibility: v }))}>
                  <SelectTrigger data-testid="edit-visibility"><SelectValue /></SelectTrigger>
                  <SelectContent>{VISIBILITIES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="mb-1.5 block text-[13px]">Notes</Label><Textarea rows={2} value={editForm.notes} onChange={(e) => setEditForm((p) => ({ ...p, notes: e.target.value }))} data-testid="edit-notes" /></div>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setEditDoc(null)}>Cancel</Button>
              <Button type="submit" className="text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="save-edit-button">Save changes</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
